import { useQuery } from "@tanstack/react-query"
import { common } from "@utils/common"
import { requestApi2 } from "@utils/http"
import { endpoints, FuelPageOpModeEventData } from "api"

type FuelEventsMetaDataQueryArgs = {
	plantId: number
	opModesId: number[]
	gte: Date
	lt: Date
}

export function useFuelEventsMetaDataQuery(args: FuelEventsMetaDataQueryArgs) {
	return useQuery({
		queryKey: [
			`queryId=FuelEventsMetaData`,
			`endpoints=[${endpoints.fuelPageGetEvents.getName()}]`,
			`plantId=${args.plantId}`,
			`opModesId=[${args.opModesId.sort()}]`,
			`gte=${args.gte?.toISOString !== undefined ? args.gte?.toISOString() : args.gte}`,
			`lt=${args.lt?.toISOString !== undefined ? args.lt?.toISOString() : args.lt}`,
		],
		queryFn: async (): Promise<FuelPageOpModeEventData[]> => {
			const validOpModesId = args.opModesId.filter((opModeId) => opModeId !== -1)
			if (validOpModesId !== undefined && validOpModesId.length === 0) {
				throw new Error(
					"Invalid query params - at least one valid opModeId must be provided"
				)
			}
			const response = await requestApi2(endpoints.fuelPageGetEvents, {
				plantId: args.plantId,
				opModeId: validOpModesId,
				interval: {
					t1: args.gte,
					t2: args.lt,
				},
			})
			const shorePowerId = response?.[0]?.metaData.loadSensorsId.find(
				(id) =>
					common.sensorDataFromIds
						.get(id)
						?.display_name.toLowerCase()
						.includes("shore power") === true
			)

			const powerConsumers = await requestApi2(endpoints.fuelPageGetPowerConsumers, {
				plantId: args.plantId,
			})
			if (powerConsumers !== null && response !== null) {
				const validPowerConsumers = powerConsumers
					.filter((consumer) =>
						["thrusters", "propulsion", "other"].includes(
							consumer.powerConsumerGroupId.toLowerCase()
						)
					)
					.map((c) => c.sensorId)

				return response.map((e) => {
					return {
						...e,
						metaData: {
							...e.metaData,
							loadSensorsId:
								shorePowerId !== undefined
									? [...validPowerConsumers, shorePowerId]
									: validPowerConsumers,
						},
					}
				})
			}
			return response !== null ? response : []
		},
		enabled: args.opModesId.filter((opModeId) => opModeId !== -1).length > 0,
		staleTime: 600000,
		refetchInterval: 600000,
	})
}
